import { IonButton, IonIcon, IonInput, IonItem } from '@ionic/react';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import i18next from 'i18next';
import { addCircleOutline, closeCircleOutline } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useMutateConfirmDeliveryOutcome } from '../../../api/hooks/delivery/useMutateConfirmDeliveryOutcome';
import { Loading } from '../../../components/atoms/Loading';
import { DeliveryStatusByQrUrl } from './DeliveryStatusByQrUrl';
import { ProviderInvoiceDoc } from './ProviderInvoiceDoc';
import { BSelect } from '../../../components/molecules/BSelect';
import { QrScanButton } from '../../../components/molecules/QrScanButton';
import { TEST_IDS } from '../../../config/utils/constants';
import { disableSubmitOnEnter } from '../../../config/utils/formUtils';
import { CONFIRM_DELIVERY_OUTCOME, PackagingType } from '../../../interfaces/myDataDelivery.interface';
import { getDeliveryOutcomeOptions, getPackagingTypeOptions } from '../../../services/utils/deliveryStatusUtils';

const emptyPackagingItem = { packagingType: null, quantity: '', otherPackagingTypeTitle: '' };

const sanitizeEnumValue = (val: any): string | undefined => {
  if (!val) return undefined;
  if (typeof val === 'string') return val;
  return val.value ?? val.id ?? undefined;
};

const sanitizePackagingType = (val: any): number | undefined => {
  if (val === null || val === undefined) return undefined;
  if (typeof val === 'object') {
    const candidate = val.value ?? val.id ?? val;
    const asNum = Number(candidate);
    return Number.isNaN(asNum) ? undefined : asNum;
  }
  const asNum = Number(val);
  return Number.isNaN(asNum) ? undefined : asNum;
};

const isOtherPackagingType = (val: any): boolean => {
  return sanitizePackagingType(val) === PackagingType.OTHER;
};

const validationSchema = yup.object().shape({
  qrUrl: yup.string().trim().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  outcome: yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  deliveredWithoutRecipient: yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  deliveredPackaging: yup.array().when('outcome', {
    is: (val: any) => sanitizeEnumValue(val) === CONFIRM_DELIVERY_OUTCOME.PARTIAL,
    then: (schema) =>
      schema
        .of(
          yup.object().shape({
            packagingType: yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
            quantity: yup
              .number()
              .typeError(i18next.t('FORMS.ERRORS.REQUIRED_FIELD'))
              .required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD'))
              .positive(i18next.t('FORMS.ERRORS.REQUIRED_FIELD'))
              .integer(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
            otherPackagingTypeTitle: yup.string().when('packagingType', {
              is: (val: any) => isOtherPackagingType(val),
              then: (s) => s.trim().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
              otherwise: (s) => s.nullable(),
            }),
          }),
        )
        .min(1, i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
    otherwise: (schema) => schema.nullable(),
  }),
});

interface Props {
  mark?: string;
  qrUrl?: string;
  isUserCounterpart?: boolean;
  titleKey?: string;
}

export const DeliveryConfirmOutcomeForm = ({ mark, qrUrl, isUserCounterpart, titleKey = 'DELIVERY.CONFIRM_OUTCOME_TITLE' }: Props) => {
  const { t } = useTranslation();
  const [committedQrUrl, setCommittedQrUrl] = useState<string | undefined>(qrUrl || undefined);
  const outcomeOptions = useMemo(() => getDeliveryOutcomeOptions(isUserCounterpart), [isUserCounterpart]);
  const outcomeSelectOptions = useMemo(
    () => outcomeOptions.map((o) => ({ id: o.value, name: t(o.label) })),
    [outcomeOptions, t],
  );
  const packagingTypeOptions = useMemo(() => getPackagingTypeOptions(), []);
  const packagingTypeSelectOptions = useMemo(
    () => packagingTypeOptions.map((o) => ({ id: o.value, name: t(o.label) })),
    [packagingTypeOptions, t],
  );
  const yesNoOptions = useMemo(
    () => [
      { id: 'false', name: t('DELIVERY.NO') },
      { id: 'true', name: t('DELIVERY.YES') },
    ],
    [t],
  );

  const { mutate, isLoading } = useMutateConfirmDeliveryOutcome();

  const formik = useFormik({
    initialValues: {
      mark,
      qrUrl: qrUrl ?? '',
      outcome: outcomeSelectOptions[0],
      deliveredWithoutRecipient: yesNoOptions[0],
      deliveredPackaging: [{ ...emptyPackagingItem }],
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const outcome = sanitizeEnumValue(values.outcome) as CONFIRM_DELIVERY_OUTCOME;
      const deliveredWithoutRecipientRaw = sanitizeEnumValue(values.deliveredWithoutRecipient);
      const deliveredWithoutRecipient = deliveredWithoutRecipientRaw === 'true';

      const deliveredPackaging =
        outcome === CONFIRM_DELIVERY_OUTCOME.PARTIAL
          ? values.deliveredPackaging.map((item) => ({
              packagingType: sanitizePackagingType(item.packagingType) as PackagingType,
              quantity: Number(item.quantity),
              otherPackagingTypeTitle: isOtherPackagingType(item.packagingType)
                ? item.otherPackagingTypeTitle || undefined
                : undefined,
            }))
          : undefined;

      mutate(
        { mark: values.mark, qrUrl: values.qrUrl, outcome, deliveredWithoutRecipient, deliveredPackaging },
        {
          onSuccess: () => {
            setCommittedQrUrl(values.qrUrl);
            resetForm();
          },
        },
      );
    },
  });

  const isPartial = sanitizeEnumValue(formik.values.outcome) === CONFIRM_DELIVERY_OUTCOME.PARTIAL;

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [shouldFocusLast, setShouldFocusLast] = useState(false);

  useEffect(() => {
    if (shouldFocusLast) {
      const lastIndex = formik.values.deliveredPackaging.length - 1;
      setTimeout(() => {
        const input = itemRefs.current[lastIndex]?.querySelector<HTMLInputElement>(
          '.ant-select-selection-search-input',
        );
        input?.focus();
      });
      setShouldFocusLast(false);
    }
  }, [shouldFocusLast, formik.values.deliveredPackaging.length]);

  const getPackagingItems = useCallback(() => {
    return formik.values.deliveredPackaging.map((item, index) => {
      const hasQuantityError =
        formik.touched.deliveredPackaging?.[index] && !!(formik.errors.deliveredPackaging?.[index] as any)?.quantity;
      const hasOtherTitleError =
        formik.touched.deliveredPackaging?.[index] &&
        !!(formik.errors.deliveredPackaging?.[index] as any)?.otherPackagingTypeTitle;
      const showOtherTitle = isOtherPackagingType(item.packagingType);

      // BSelect reads formik.values[field.name] directly, so we proxy the item fields at flat keys
      const itemFormik = {
        ...formik,
        values: { packagingType: item.packagingType },
        setFieldValue: (name: string, value: any) => {
          formik.setFieldValue(`deliveredPackaging[${index}].${name}`, value);
        },
        touched: {},
        errors: {},
      };

      return (
        <div
          key={index}
          ref={(el) => {
            itemRefs.current[index] = el;
          }}
          className="flex-center-center-row-wrap gap-12 simpleBorder p-12 positionRelative">
          <BSelect
            field={{
              name: 'packagingType',
              label: 'PACKAGING_TYPE_LABEL',
              placeholder: 'PACKAGING_TYPE_LABEL',
              allowClear: false,
              labelStyle: 'p-b-12 p-l-6',
              data: packagingTypeSelectOptions,
            }}
            formik={itemFormik}
            isRequired={true}
            widthClass="width-300px"
            testId={`${TEST_IDS.Delivery.packagingTypeSelect}-${index}`}
          />

          <div>
            <div className="p-b-12 p-l-6">{t('DELIVERY.PACKAGING_QUANTITY')}</div>
            <IonItem
              className={`width-300px height-50px border-radius-input g-select-border ${
                hasQuantityError ? 'b-field-border-error' : ''
              }`}
              mode="md">
              <IonInput
                className="height-48px"
                mode="md"
                type="number"
                step="1"
                name={`deliveredPackaging[${index}].quantity`}
                placeholder={t('DELIVERY.PACKAGING_QUANTITY') + '...'}
                onIonInput={(e: any) => {
                  const raw = e?.detail?.value;
                  const intVal = raw ? String(Math.trunc(Number(raw))) : raw;
                  formik.setFieldValue(`deliveredPackaging[${index}].quantity`, intVal);
                }}
                onIonBlur={() => formik.setFieldTouched(`deliveredPackaging[${index}].quantity`, true)}
                value={item.quantity}
                onKeyPress={disableSubmitOnEnter}
              />
            </IonItem>
          </div>

          {showOtherTitle && (
            <div>
              <div className="p-b-12 p-l-6">{t('DELIVERY.PACKAGING_OTHER_TITLE')}</div>
              <IonItem
                className={`width-300px height-50px border-radius-input g-select-border ${
                  hasOtherTitleError ? 'b-field-border-error' : ''
                }`}
                mode="md">
                <IonInput
                  className="height-48px"
                  mode="md"
                  type="text"
                  name={`deliveredPackaging[${index}].otherPackagingTypeTitle`}
                  placeholder={t('DELIVERY.PACKAGING_OTHER_TITLE') + '...'}
                  onIonInput={(e: any) =>
                    formik.setFieldValue(`deliveredPackaging[${index}].otherPackagingTypeTitle`, e?.detail?.value)
                  }
                  onIonBlur={() => formik.setFieldTouched(`deliveredPackaging[${index}].otherPackagingTypeTitle`, true)}
                  value={item.otherPackagingTypeTitle}
                  onKeyPress={disableSubmitOnEnter}
                />
              </IonItem>
            </div>
          )}

          {formik.values.deliveredPackaging.length > 1 && (
            <FormikProvider value={formik}>
              <FieldArray
                name="deliveredPackaging"
                render={({ remove }) => (
                  <IonButton
                    fill="clear"
                    onClick={() => remove(index)}
                    className="ion-no-padding"
                    style={{ position: 'absolute', top: 4, right: 4, margin: 0 }}>
                    <IonIcon icon={closeCircleOutline} size="large" color="danger" />
                  </IonButton>
                )}
              />
            </FormikProvider>
          )}
        </div>
      );
    });
  }, [formik, packagingTypeSelectOptions, t]);

  return (
    <>
      {isLoading && <Loading global />}
      <div className="width-100 flex-center-center-column-nowrap">
        <form onSubmit={formik.handleSubmit}>
          <div className="simpleBorder p-12">
            <div className="boldText5 margin-b-24 text-center">{t(titleKey)}</div>

            <div className="flex-center-center-row-wrap gap-12 p-b-12">
              {!qrUrl && (
                <div>
                  <div className="p-b-12 p-l-6 flex-start-center-row-nowrap gap-6">
                    {t('DELIVERY.QR_TITLE')}
                    <QrScanButton onScan={(url) => formik.setFieldValue('qrUrl', url)} />
                  </div>
                  <IonItem
                    className={`width-300px height-50px border-radius-input g-select-border ${
                      formik.touched.qrUrl && formik.errors.qrUrl ? 'b-field-border-error' : ''
                    }`}
                    mode="md">
                    <IonInput
                      className="height-48px"
                      mode="md"
                      type="text"
                      name="qrUrl"
                      placeholder={t('DELIVERY.QR_PLACEHOLDER') + '...'}
                      onIonInput={(e: any) => formik.setFieldValue('qrUrl', e?.detail?.value)}
                      onIonBlur={() => {
                        formik.setFieldTouched('qrUrl', true);
                        setCommittedQrUrl(formik.values.qrUrl || undefined);
                      }}
                      value={formik.values.qrUrl}
                      onKeyPress={disableSubmitOnEnter}
                    />
                  </IonItem>
                </div>
              )}

              <div>
                <BSelect
                  field={{
                    name: 'outcome',
                    label: 'OUTCOME_RESULT',
                    placeholder: 'OUTCOME_RESULT',
                    allowClear: false,
                    labelStyle: 'p-b-12 p-l-6',
                    data: outcomeSelectOptions,
                  }}
                  formik={formik}
                  isRequired={true}
                  widthClass="width-300px"
                  testId={TEST_IDS.Delivery.confirmOutcomeSelect}
                />
              </div>

              <div>
                <BSelect
                  field={{
                    name: 'deliveredWithoutRecipient',
                    label: 'OUTCOME_WITHOUT_RECIPIENT',
                    placeholder: 'OUTCOME_WITHOUT_RECIPIENT',
                    allowClear: false,
                    labelStyle: 'p-b-12 p-l-6',
                    data: yesNoOptions,
                  }}
                  formik={formik}
                  isRequired={true}
                  widthClass="width-300px"
                  testId={TEST_IDS.Delivery.deliveredWithoutRecipientSelect}
                />
              </div>
            </div>

            {isPartial && (
              <div className="m-t-12">
                <div className="boldText5 margin-b-12 p-l-6">{t('DELIVERY.OUTCOME_PACKAGING')}</div>
                <div className="flex-center-center-column-nowrap gap-12">
                  {getPackagingItems()}
                  <FormikProvider value={formik}>
                    <FieldArray
                      name="deliveredPackaging"
                      render={({ push }) => (
                        <IonButton
                          fill="clear"
                          onClick={() => {
                            push({ ...emptyPackagingItem });
                            setShouldFocusLast(true);
                          }}>
                          <IonIcon icon={addCircleOutline} size="large" color="success" />
                          <span className="p-l-6">{t('DELIVERY.ADD_PACKAGING')}</span>
                        </IonButton>
                      )}
                    />
                  </FormikProvider>
                </div>
              </div>
            )}
          </div>

          <div className="flex-center-center-column-nowrap m-t-30 width-100">
            <div className="max-width-460px">
              <IonButton
                mode="md"
                type="submit"
                disabled={!formik.isValid || isLoading}
                className="button-focused width-100">
                {t('DELIVERY.CONFIRM_OUTCOME_SUBMIT')}
              </IonButton>
            </div>
          </div>
        </form>
        <ProviderInvoiceDoc qrUrl={formik.values.qrUrl} />
        {/* When `mark` is already known, the parent screen already fetched and renders this same
            status/doc data via useDeliveryStatusByMark -- avoid a duplicate qr-status request */}
        {!mark && <DeliveryStatusByQrUrl qrUrl={committedQrUrl} />}
      </div>
    </>
  );
};
